/**
 * useRoute - czyta aktualny URL i zwraca NavRoute (kompatybilne z new-design routing).
 * Plus navigate() helper ktory bierze NavRoute i wola useNavigate(routeToUrl(r)).
 */
import { useMemo, useCallback } from "react";
import { useLocation, useNavigate as useReactRouterNavigate, useParams, useSearchParams } from "react-router-dom";
import type { NavRoute, RouteName } from "@/lib/data";
import { ROUTE_SECTION, SECTION_DEFAULT } from "@/lib/data";
import { routeToUrl, urlToRouteName } from "@/lib/routing";

export function useRoute() {
  const location = useLocation();
  const reactNavigate = useReactRouterNavigate();
  const params = useParams<{ toolSlug?: string; projectId?: string }>();
  const [searchParams] = useSearchParams();

  const route = useMemo<NavRoute>(() => {
    const name = urlToRouteName(location.pathname) ?? "hub";
    const r: NavRoute = { name };
    if (name === "tool" && params.toolSlug) r.toolId = params.toolSlug;
    if (name === "project" && params.projectId) r.projectId = params.projectId;
    const tab = searchParams.get("tab");
    const filter = searchParams.get("filter");
    if (tab) r.tab = tab;
    if (filter) r.filter = filter;
    return r;
  }, [location.pathname, params, searchParams]);

  const sectionId = useMemo(() => ROUTE_SECTION[route.name] ?? "studio", [route.name]);

  const navigate = useCallback(
    (target: NavRoute) => {
      reactNavigate(routeToUrl(target));
    },
    [reactNavigate],
  );

  const goSection = useCallback(
    (id: string) => {
      const target = SECTION_DEFAULT[id] ?? { name: "hub" as RouteName };
      navigate(target);
    },
    [navigate],
  );

  return { route, sectionId, navigate, goSection };
}
